import React, { useMemo, useState } from "react";
import { Modal, Button, Collapse } from "react-bootstrap";

const fmt = (v) => Number(v || 0).toLocaleString("vi-VN") + "₫";

function ShiftSummaryModal({
  show,
  onClose,
  data,
  title = "📊 Báo Cáo Doanh Thu",
  totalLabel = "Doanh thu hiện tại",
  emptyMessage = "Chưa có dữ liệu.",
  filterControls = null,
  isLoading = false,
  printTitle = "Báo Cáo Doanh Thu",
}) {
  const [openDates, setOpenDates] = useState({});

  // Giữ logic cũ nếu data là mảng (group theo ngày)
  const totalsByDay = useMemo(() => {
    if (!Array.isArray(data)) return {};
    return data.reduce((acc, day) => {
      const sum = (day.Ca || []).reduce(
        (s, c) => s + Number(c.TienSauGiamGia || 0),
        0
      );
      acc[day.Ngay] = sum;
      return acc;
    }, {});
  }, [data]);

  const toggle = (date) => setOpenDates((p) => ({ ...p, [date]: !p[date] }));

  const renderObjectView = (d) => {
    const pay = d.ChiTietThanhToan || {};
    const monthLabel =
      d.thang && d.nam ? `Tháng ${d.thang}/${d.nam}` : null;

    return (
      <div className="mb-3 border rounded p-3">
        <div>
          <div>
            <div className="mb-3">
              {monthLabel ? (
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {monthLabel}
                </div>
              ) : null}
              <div className="text-muted" style={{ fontSize: 13 }}>
                {totalLabel}
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#0d6efd" }}>
                {fmt(d.TongTien)}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-3">
          <div className="fw-bold mb-2" style={{ fontSize: 14 }}>
            Chi tiết thanh toán
          </div>
          <div className="row g-2" style={{ fontSize: 13 }}>
            <div className="col-12 col-sm-6">
              <span className="text-muted">Tiền mặt:</span>{" "}
              <strong>{fmt(pay.TienMat)}</strong>
            </div>
            <div className="col-12 col-sm-6">
              <span className="text-muted">Chuyển khoản:</span>{" "}
              <strong>{fmt(pay.ChuyenKhoan)}</strong>
            </div>
            <div className="col-12 col-sm-6">
              <span className="text-muted">VISA:</span>{" "}
              <strong>{fmt(pay.VISA)}</strong>
            </div>
            <div className="col-12 col-sm-6">
              <span className="text-muted">MOMO:</span>{" "}
              <strong>{fmt(pay.MOMO)}</strong>
            </div>
            <div className="col-12 col-sm-6">
              <span className="text-muted">VNPay:</span>{" "}
              <strong>{fmt(pay.VNPay)}</strong>
            </div>
            <div className="col-12 col-sm-6">
              <span className="text-muted">Grab:</span>{" "}
              <strong>{fmt(pay.Grab)}</strong>
            </div>
            <div className="col-12 col-sm-6">
              <span className="text-muted">Shopee:</span>{" "}
              <strong>{fmt(pay.Shopee)}</strong>
            </div>
            <div className="col-12 col-sm-6">
              <span className="text-muted">Khách hàng nợ:</span>{" "}
              <strong className="text-danger">{fmt(pay.KhachHangNo)}</strong>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const buildPrintHtml = () => {
    const header = `
      <div style="text-align:center;font-family:Arial;margin-bottom:12px;">
        <h3 style="margin:0;">${printTitle}</h3>
        <div style="font-size:12px;color:#555;">In lúc: ${new Date().toLocaleString()}</div>
      </div>
      <hr/>
    `;

    if (!data) return header + `<div>${emptyMessage}</div>`;

    // Trường hợp nhiều ngày
    if (Array.isArray(data)) {
      const daysHtml = data
        .map((day) => {
          const caHtml = (day.Ca || [])
            .map(
              (c) => `
                <div style="margin:6px 0;padding:6px;border:1px dashed #ccc;border-radius:6px;font-size:13px;">
                  <div><strong>Ca:</strong> ${c.CaID || ""} • <strong>NV:</strong> ${c.TenNhanVien || ""}</div>
                  <div style="color:#555;">Giờ kết ca: ${c.ThoiGianKetCa || ""}</div>
                  <div><strong>Sau giảm:</strong> ${fmt(c.TienSauGiamGia)}</div>
                  <div style="color:#555;">Tổng: ${fmt(c.TongTien)} • CK: ${fmt(c.GiamGia)}</div>
                </div>
              `
            )
            .join("");
          return `
            <div style="margin-bottom:12px;font-family:Arial;">
              <div style="font-size:15px;font-weight:700;">${day.Ngay}</div>
              <div style="font-size:13px;color:#555;">Tổng ngày: ${fmt(totalsByDay[day.Ngay])}</div>
              ${caHtml || "<div style='color:#999;'>Không có ca.</div>"}
            </div>
            <hr/>
          `;
        })
        .join("");
      return header + daysHtml;
    }

    // Trường hợp object đơn
    const pay = data.ChiTietThanhToan || {};
    const monthLabel =
      data.thang && data.nam
        ? `<div style="font-size:12px;color:#555;">Tháng ${data.thang}/${data.nam}</div>`
        : "";
    const payments = [
      ["Tiền mặt", pay.TienMat],
      ["Chuyển khoản", pay.ChuyenKhoan],
      ["VISA", pay.VISA],
      ["MOMO", pay.MOMO],
      ["VNPay", pay.VNPay],
      ["Grab", pay.Grab],
      ["Shopee", pay.Shopee],
      ["Khách hàng nợ", pay.KhachHangNo],
    ]
      .map(
        ([k, v]) =>
          `<div style="display:flex;justify-content:space-between;font-size:13px;"><span>${k}:</span><strong>${fmt(v)}</strong></div>`
      )
      .join("");
    return (
      header +
      `
      <div style="font-family:Arial;font-size:14px;">
        <div style="margin-bottom:10px;">
          ${monthLabel}
          <div style="color:#555;">${totalLabel}</div>
          <div style="font-size:20px;font-weight:700;color:#0d6efd;">${fmt(data.TongTien)}</div>
        </div>
        <div style="font-weight:700;margin-bottom:6px;">Chi tiết thanh toán</div>
        ${payments}
      </div>`
    );
  };

  const handlePrint = () => {
    const win = window.open("", "_blank", "width=700,height=900");
    if (!win) return;
    win.document.write(`
      <html>
        <head>
          <title>${printTitle}</title>
          <style>body{margin:12px;}</style>
        </head>
        <body>${buildPrintHtml()}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.onafterprint = () => win.close();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="md"
      dialogClassName="modal-dialog-scrollable"
      contentClassName="rounded-4"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontWeight: 700 }}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light">
        {filterControls}
        {isLoading ? (
          <div className="text-muted">Đang tải dữ liệu...</div>
        ) : !data ? (
          <div className="text-muted">{emptyMessage}</div>
        ) : Array.isArray(data) ? (
          data.map((day) => (
            <div
              key={day.Ngay}
              className="mb-3 p-2 border rounded-3 shadow-sm bg-white"
            >
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => toggle(day.Ngay)}
              >
                <div>
                  <strong style={{ fontSize: 16, fontWeight: 700 }}>
                    {day.Ngay}
                  </strong>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Tổng ngày:{" "}
                    <strong className="text-dark">
                      {totalsByDay[day.Ngay]?.toLocaleString()}₫
                    </strong>
                  </div>
                </div>
                <span className="text-muted">
                  {openDates[day.Ngay] ? "▾" : "▸"}
                </span>
              </div>
              <Collapse in={!!openDates[day.Ngay]}>
                <div className="mt-2">
                  {(day.Ca || []).map((c, idx) => (
                    <div
                      key={idx}
                      className="border-top pt-2 mt-2 d-flex flex-column gap-1"
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div style={{ fontSize: 14 }}>
                            <span className="text-muted">Ca:</span>{" "}
                            <strong style={{ fontWeight: 700 }}>
                              {c.CaID}
                            </strong>{" "}
                            • <span className="text-muted">NV:</span>{" "}
                            <strong>{c.TenNhanVien}</strong>
                          </div>
                          <div
                            className="text-muted d-flex flex-wrap gap-1"
                            style={{ fontSize: 12 }}
                          >
                            <span className="badge bg-light text-dark border">
                              Giờ kết ca: {c.ThoiGianKetCa}
                            </span>
                            {c.DSBanhKem ? (
                              <span className="badge bg-light text-dark border">
                                Bánh kem: {c.DSBanhKem}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className="text-end">
                          <div style={{ fontSize: 13 }} className="text-muted">
                            Sau giảm
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 700 }} className="text-primary">
                            {Number(c.TienSauGiamGia || 0).toLocaleString()}₫
                          </div>
                          <div className="text-muted" style={{ fontSize: 11 }}>
                            Tổng:{" "}
                            <strong className="text-dark">
                              {Number(c.TongTien || 0).toLocaleString()}₫
                            </strong>{" "}
                            • CK:{" "}
                            <strong className="text-dark">
                              {Number(c.GiamGia || 0).toLocaleString()}₫
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Collapse>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-3 shadow-sm p-2">
            {renderObjectView(data)}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button variant="outline-primary" onClick={handlePrint} disabled={isLoading}>
          In báo cáo
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Đóng
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ShiftSummaryModal;
